// Language detection logic for outgoing chat messages
// - Wraps the global Web Worker based detector
// - Falls back to the last own message language in the current thread when confidence is low

import { detectLanguage } from '@/modules/global/langDetection'
import store from '@/store'

export const LANGUAGE_PROB_THRESHOLD = 0.85

function isMeId(userId) {
  return parseInt(userId) === store.getters['auth/userId']
}

// Find the last message language sent by current user in this thread.
// Tries to load older messages if necessary
export async function getLastOwnMessageLanguage(chatThreadStore) {
  // Scan a merged view (cache + UI) from newest to oldest
  function findLastOwnMessageLanguage() {
    const merged = chatThreadStore.combinedMessages
    const found = merged
      .slice() // do not mutate original
      .reverse()
      .find((msg) => {
        return isMeId(msg.senderDisplayName) && msg?.metadata?.language
      })
    return found ? found.metadata.language : null
  }

  let lastOwnMessageLanguage = findLastOwnMessageLanguage()
  if (lastOwnMessageLanguage) {
    return lastOwnMessageLanguage
  }

  let attempts = 0
  while (!chatThreadStore.allMessagesAreLoaded && attempts < 3) {
    // eslint-disable-next-line no-await-in-loop
    await chatThreadStore.fetchOlderMessagesIntoCache(20)
    attempts += 1
    lastOwnMessageLanguage = findLastOwnMessageLanguage()
    if (lastOwnMessageLanguage) {
      return lastOwnMessageLanguage
    }
  }

  return null
}

// Decide the language for a message chunk based on detection results and fallback.
// Returns a BCP-47 language code string or null.
export async function detectLanguageForChunk(text, chatThreadStore, { threshold = LANGUAGE_PROB_THRESHOLD } = {}) {
  try {
    const detected = await detectLanguage(text)

    if (detected.length > 0) {
      const top = detected[0]
      const prob = top.probability
      if (prob >= threshold) {
        return top.languageCode
      }
    }

    const fallback = await getLastOwnMessageLanguage(chatThreadStore)

    return fallback
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
    return null
  }
}
